import { useAuthor } from '@gik/api/author/author';
import { useAuthorPosts } from '@gik/api/posts/authorPosts';
import { ArticleDynamicLoader } from '@gik/blog/components/ArticleDynamicLoader/ArticleDynamicLoader';
import { ArticlesGrid } from '@gik/blog/components/ArticlesGrid/ArticlesGrid';
import { AuthorInfo } from '@gik/blog/components/AuthorInfo/AuthorInfo';
import type Article from '@gik/blog/models/Article';
import type Author from '@gik/blog/models/Author';
import { withAuthorMetaTags } from '@gik/blog/utils/MetaTags';
import WordpressConverter from '@gik/blog/utils/WordpressConverter';
import { DynamicComponent } from '@gik/core/components/DynamicComponent';
import { withTitle } from '@gik/core/hocs/withTitle';
import type WordpressAuthor from '@gik/core/models/wordpress/WordpressAuthor';
import type WordpressPost from '@gik/core/models/wordpress/WordpressPost';
import { useBemCN } from '@gik/core/utils/bemBlock';
import { AuthorStructuredData } from '@gik/seo';
import { PageSection } from '@gik/ui/gik/PageSection';
import { HTMLParser } from '@gik/ui/HTMLParser';
import React from 'react';

export type IAuthorPageServerProps = {
  author?: WordpressAuthor[];
  authorPosts?: WordpressPost[];
};

export type IAuthorPageProps = {
  author: Author;
};

const blockName = 'author-page';

function AuthorPageComp({ author }: IAuthorPageProps) {
  const bem = useBemCN(blockName);

  // TODO: i18n
  return withAuthorMetaTags(
    author,
    <AuthorStructuredData author={author}>
      <article {...bem()}>
        <PageSection {...bem('author-info-section')} noPadBottom>
          <AuthorInfo author={author} />
        </PageSection>

        <PageSection {...bem('author-bio-section')} noPadTop noPadBottom>
          <HTMLParser rawHtml={author.bio} />
        </PageSection>

        <PageSection>
          <h2 {...bem('more-title')}>More from this author</h2>
          <DynamicComponent<WordpressPost[], Article[]>
            height={200}
            fetch={function useFetch() {
              return useAuthorPosts(author.slug);
            }}
            transform={data =>
              data.map(post => WordpressConverter.convertPost(post)).filter(WordpressConverter.filterOutPreviewContent)
            }
            loadingComponent={() => <ArticlesGrid articles={Array.from(new Array(4))} skeleton />}
          >
            {({ transformed: articles }) => <ArticleDynamicLoader articles={articles} />}
          </DynamicComponent>
        </PageSection>
      </article>
    </AuthorStructuredData>
  );
}

export function AuthorPage({
  slug,
  ...otherProps
}: Omit<React.ComponentProps<typeof AuthorPageComp>, 'author'> & { slug: string }) {
  return (
    <DynamicComponent<WordpressAuthor[], Author>
      height={1000}
      fetch={function useFetch() {
        return useAuthor(slug);
      }}
      transform={data => WordpressConverter.convertAuthor(data[0])}
    >
      {({ transformed: author }) =>
        withTitle(
          {
            title: author.fullName,
          },
          <AuthorPageComp author={author} {...otherProps} />
        )
      }
    </DynamicComponent>
  );
}
